import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Input, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";
import NotificationConfigService from "../../services/notificationConfig";
import Breadcrumbs from "../../components/Breadcrumb";
import { CompanyId } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";

const DELIVERY_CHANNELS = [
  { value: 0, translationKey: "ADMIN.CHANNEL_NONE" },
  { value: 1, translationKey: "ADMIN.CHANNEL_SMS" },
  { value: 2, translationKey: "ADMIN.CHANNEL_WHATSAPP" },
  { value: 3, translationKey: "ADMIN.CHANNEL_BOTH" },
];

const UserNotificationPreferences = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: t("ADMIN.TITLE"), link: "#" },
    { title: t("ADMIN.USER_NOTIFICATION_PREFERENCES"), link: "#" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPreferences = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const response = await NotificationConfigService.getUserNotificationPreferences(companyId);
      if (response.data && response.data.status === 200 && response.data.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user notification preferences:", error);
      toast.error(t("ADMIN.LOAD_ERROR"));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleChannelChange = (userId, value) => {
    setUsers((prev) =>
      prev.map((user) =>
        user.userId === userId
          ? { ...user, deliveryChannel: parseInt(value) }
          : user
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    dispatch(setLoadingOverlay(true));
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const response = await NotificationConfigService.saveBulkUserNotificationPreferences({
        companyId,
        preferences: users.map((u) => ({
          userId: u.userId,
          deliveryChannel: u.deliveryChannel,
        })),
      });
      if (response.data && response.data.status === 200) {
        toast.success(t("ADMIN.SAVE_SUCCESS"));
      } else {
        toast.error(response.data?.message || t("ADMIN.SAVE_ERROR"));
      }
    } catch (error) {
      console.error("Error saving user notification preferences:", error);
      toast.error(t("ADMIN.SAVE_ERROR"));
    } finally {
      setIsSaving(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs
          title={t("ADMIN.USER_NOTIFICATION_PREFERENCES")}
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            <h4 className="card-title mb-4">{t("ADMIN.USER_NOTIFICATION_PREFERENCES")}</h4>

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-4 text-muted">
                {t("ADMIN.NO_USERS_FOUND")}
              </div>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("ADMIN.USER_NAME")}</th>
                    <th>{t("ADMIN.ROLE")}</th>
                    <th>{t("ADMIN.PHONE_NUMBER")}</th>
                    <th className="text-center">{t("ADMIN.DELIVERY_CHANNEL")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.userId}>
                      <td>{index + 1}</td>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.roleName}</td>
                      <td>{user.phoneNumber}</td>
                      <td className="text-center">
                        <Input
                          type="select"
                          value={user.deliveryChannel}
                          onChange={(e) =>
                            handleChannelChange(user.userId, e.target.value)
                          }
                          style={{ minWidth: "140px", display: "inline-block", width: "auto" }}
                        >
                          {DELIVERY_CHANNELS.map((channel) => (
                            <option key={channel.value} value={channel.value}>
                              {t(channel.translationKey)}
                            </option>
                          ))}
                        </Input>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <div className="mt-4">
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isSaving || isLoading || users.length === 0}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("SAVING")}
                  </>
                ) : (
                  t("SAVE_TITLE")
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default UserNotificationPreferences;
